import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import ContextualAIChat from "@/components/ContextualAIChat";
import { 
  ArrowLeft, 
  Plus, 
  Target,
  Trash2,
  Edit2,
  Loader2,
  Trophy,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoalsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoal, setNewGoal] = useState({
    title: "",
    target_amount: "",
    current_amount: 0,
    deadline: ""
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchGoals(userData.id);
  }, [navigate]);

  const fetchGoals = async (userId) => {
    try {
      const response = await axios.get(`${API}/goals/${userId}`);
      setGoals(response.data || []);
    } catch (error) {
      console.error("Error fetching goals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async () => {
    if (!newGoal.title || !newGoal.target_amount) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/goals/${user.id}`, {
        title: newGoal.title,
        target_amount: parseFloat(newGoal.target_amount),
        current_amount: parseFloat(newGoal.current_amount) || 0,
        deadline: newGoal.deadline || null
      });
      
      toast.success("Goal created successfully!");
      setDialogOpen(false);
      setNewGoal({ title: "", target_amount: "", current_amount: 0, deadline: "" });
      fetchGoals(user.id);
    } catch (error) {
      toast.error("Failed to create goal");
    }
  };

  const handleUpdateProgress = async (goalId, newAmount) => {
    try {
      await axios.put(`${API}/goals/${goalId}`, {
        current_amount: newAmount
      });
      fetchGoals(user.id);
    } catch (error) {
      toast.error("Failed to update goal");
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/goals/${goalId}`);
      toast.success("Goal deleted");
      fetchGoals(user.id);
    } catch (error) {
      toast.error("Failed to delete goal");
    }
  };

  const totalTarget = goals.reduce((sum, g) => sum + (g.target_amount || 0), 0);
  const totalCurrent = goals.reduce((sum, g) => sum + (g.current_amount || 0), 0);
  const overallProgress = totalTarget > 0 ? (totalCurrent / totalTarget) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-heading text-xl font-semibold">Financial Goals</h1>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-goal-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Create New Goal</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Goal Title</Label>
                  <Input
                    placeholder="e.g., Emergency Fund, Vacation, New Car"
                    value={newGoal.title}
                    onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                    className="mt-2"
                    data-testid="goal-title-input"
                  />
                </div>
                <div>
                  <Label>Target Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="10000"
                    value={newGoal.target_amount}
                    onChange={(e) => setNewGoal({ ...newGoal, target_amount: e.target.value })}
                    className="mt-2"
                    data-testid="goal-target-input"
                  />
                </div>
                <div>
                  <Label>Current Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={newGoal.current_amount}
                    onChange={(e) => setNewGoal({ ...newGoal, current_amount: e.target.value })}
                    className="mt-2"
                    data-testid="goal-current-input"
                  />
                </div>
                <div>
                  <Label>Target Date (Optional)</Label>
                  <Input
                    type="date"
                    value={newGoal.deadline}
                    onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                    className="mt-2"
                    data-testid="goal-deadline-input"
                  />
                </div>
                <Button 
                  onClick={handleAddGoal} 
                  className="w-full rounded-full"
                  data-testid="save-goal-btn"
                >
                  Create Goal
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Overall Progress */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-border/50 mb-6 bg-gradient-to-br from-accent/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-accent/20 flex items-center justify-center">
                  <Trophy className="h-7 w-7 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Overall Progress</p>
                  <p className="font-heading text-2xl font-bold">
                    ${totalCurrent.toFixed(0)} 
                    <span className="text-base font-normal text-muted-foreground">
                      / ${totalTarget.toFixed(0)}
                    </span>
                  </p>
                </div>
              </div>
              <Progress value={overallProgress} className="h-3" />
              <p className="text-sm text-muted-foreground mt-2">
                {overallProgress.toFixed(0)}% of your goals achieved
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Goals List */}
        {goals.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-border/50 border-dashed">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center">
                  <Target className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-4">No goals set yet</p>
                <Button 
                  onClick={() => setDialogOpen(true)}
                  className="rounded-full"
                  data-testid="empty-add-goal-btn"
                >
                  Create Your First Goal
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {goals.map((goal, index) => {
              const progress = goal.target_amount > 0 
                ? (goal.current_amount / goal.target_amount) * 100 
                : 0;
              const isCompleted = progress >= 100;
              
              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`border-border/50 ${isCompleted ? 'border-accent/50 bg-accent/5' : ''}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            isCompleted ? 'bg-accent/20' : 'bg-primary/10'
                          }`}>
                            {isCompleted ? (
                              <Trophy className="h-5 w-5 text-accent" />
                            ) : (
                              <Target className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold">{goal.title}</h3>
                            {goal.deadline && (
                              <p className="text-xs text-muted-foreground">
                                Target: {new Date(goal.deadline).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteGoal(goal.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-goal-${goal.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            ${goal.current_amount.toFixed(0)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            ${goal.target_amount.toFixed(0)}
                          </span>
                        </div>
                        <Progress 
                          value={Math.min(progress, 100)} 
                          className={`h-2 ${isCompleted ? '[&>div]:bg-accent' : ''}`}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {progress.toFixed(0)}% achieved
                        </p>
                      </div>
                      
                      {!isCompleted && (
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">
                            Update Progress
                          </Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[goal.current_amount]}
                              max={goal.target_amount}
                              step={1}
                              onValueChange={([value]) => {
                                setGoals(goals.map(g => 
                                  g.id === goal.id ? { ...g, current_amount: value } : g
                                ));
                              }}
                              onValueCommit={([value]) => handleUpdateProgress(goal.id, value)}
                              className="flex-1"
                              data-testid={`goal-slider-${goal.id}`}
                            />
                            <span className="text-sm font-mono w-16 text-right">
                              ${goal.current_amount.toFixed(0)}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {isCompleted && (
                        <div className="flex items-center gap-2 text-accent">
                          <TrendingUp className="h-4 w-4" />
                          <span className="text-sm font-medium">Goal Achieved! 🎉</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Contextual AI Chat */}
      {user && (
        <ContextualAIChat
          context="goals"
          userId={user.id}
          pageData={{ 
            goals: goals.map(g => ({ title: g.title, target: g.target_amount, current: g.current_amount })),
            total_target: totalTarget,
            total_current: totalCurrent,
            overall_progress: overallProgress
          }}
          suggestedPrompts={[
            "Is my savings goal realistic?",
            "How can I reach my goal faster?",
            "What goals should I prioritize?",
            "How much should I save monthly?"
          ]}
          accentColor="tamba-teal"
        />
      )}
    </div>
  );
};

export default GoalsPage;
